import React, { useCallback, useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import { ElementDefinition } from '../../types';

interface NumericValueInputProps {
  value: string;
  onChange: (value: string) => void;
  elementDefinition?: ElementDefinition | null;
  disabled?: boolean;
  placeholder?: string;
}

const NumericValueInput: React.FC<NumericValueInputProps> = ({
  value,
  onChange,
  elementDefinition,
  disabled = false,
  placeholder = 'Number',
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [error, setError] = useState<string>('');

  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const validateNumericInput = useCallback(
    (inputValue: string): string => {
      if (!inputValue.trim()) {
        return ''; // Empty is okay, validation will happen elsewhere
      }

      // Check if it's a valid number
      const numericValue = parseFloat(inputValue);
      if (isNaN(numericValue) || !isFinite(numericValue)) {
        return 'Please enter a valid number';
      }

      // Check min/max constraints from element definition
      if (
        elementDefinition?.validation?.min !== undefined &&
        numericValue < elementDefinition.validation.min
      ) {
        return `Value must be at least ${elementDefinition.validation.min}`;
      }

      if (
        elementDefinition?.validation?.max !== undefined &&
        numericValue > elementDefinition.validation.max
      ) {
        return `Value must be at most ${elementDefinition.validation.max}`;
      }

      // Additional validation for specific element types
      if (elementDefinition?.min !== undefined && numericValue < elementDefinition.min) {
        return `Value must be at least ${elementDefinition.min}`;
      }

      if (elementDefinition?.max !== undefined && numericValue > elementDefinition.max) {
        return `Value must be at most ${elementDefinition.max}`;
      }

      return '';
    },
    [elementDefinition]
  );

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setLocalValue(newValue);

      // Validate the input
      const validationError = validateNumericInput(newValue);
      setError(validationError);

      // Only call onChange if the value is valid or empty
      if (!validationError || !newValue.trim()) {
        onChange(newValue);
      }
    },
    [onChange, validateNumericInput]
  );

  const handleBlur = useCallback(() => {
    // Ensure the parent gets the latest value on blur
    const validationError = validateNumericInput(localValue);
    if (!validationError) {
      onChange(localValue);
    }
  }, [localValue, onChange, validateNumericInput]);

  // Determine input type and step based on element definition
  const getInputProps = () => {
    const props: {
      type: string;
      step?: string | number;
      min?: number;
      max?: number;
      inputMode?: 'numeric' | 'decimal';
    } = {
      type: 'number',
      inputMode: 'numeric',
    };

    // Set step for decimal numbers
    const hasDecimalConstraints =
      (elementDefinition?.validation?.min !== undefined &&
        elementDefinition.validation.min % 1 !== 0) ||
      (elementDefinition?.validation?.max !== undefined &&
        elementDefinition.validation.max % 1 !== 0);

    if (hasDecimalConstraints) {
      props.step = '0.01';
      props.inputMode = 'decimal';
    } else {
      props.step = '1';
    }

    // Set min/max if defined
    if (elementDefinition?.validation?.min !== undefined) {
      props.min = elementDefinition.validation.min;
    } else if (elementDefinition?.min !== undefined) {
      props.min = elementDefinition.min;
    }

    if (elementDefinition?.validation?.max !== undefined) {
      props.max = elementDefinition.validation.max;
    } else if (elementDefinition?.max !== undefined) {
      props.max = elementDefinition.max;
    }

    return props;
  };

  return (
    <TextField
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      placeholder={placeholder}
      variant="outlined"
      size="small"
      error={!!error}
      helperText={
        error ||
        (elementDefinition?.validation
          ? `Range: ${elementDefinition.validation.min ?? elementDefinition.min ?? 'any'} - ${elementDefinition.validation.max ?? elementDefinition.max ?? 'any'}`
          : undefined)
      }
      inputProps={getInputProps()}
      sx={{
        fontSize: '14px',
        '& .MuiFormHelperText-root': {
          fontSize: '11px',
        },
      }}
    />
  );
};

export default NumericValueInput;
